/**
 * DoorKnock - the second game on the GAME-LEVEL-ENGINE.
 *
 * A street of doors. Say who is behind one and it opens; at Phase 3 ask for
 * them in two words; at Phase 4 say what is happening and where they go.
 *
 * =============================================================================
 * WHAT IS LEFT IN A GAME
 *
 * Content (`content/`), a mechanic (this file), and its own judgements
 * (`logic/`). Everything else is the platform's:
 *
 *   shared/trialLoop.js     the trial, and the only implementation of it
 *   shared/browserStage.js  the device: a synthesiser, a recogniser, timers
 *   shared/step.js          how many goes a step gets, and where each opens
 *   shared/pacing.js        obeying the engine's recommendation
 *   engine/*                the framework, the judgements, the scoring, the rules
 *
 * This file decides what the child SEES and what happens when they speak. It
 * decides no pacing, colours no box, numbers no attempt and fades no prompt.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import './DoorKnock.css';

import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { useAudioEffects } from '../../../hooks/useAudioEffects';

import { evaluateProgression } from '../../../engine/progression.js';
import { getLevel, getPromptStyle, getStrictness } from '../../../engine/framework.js';

import { runTrial } from '../shared/trialLoop.js';
import { createBrowserStage } from '../shared/browserStage.js';
import { createStep } from '../shared/step.js';
import { readRecommendation } from '../shared/pacing.js';
import { openingRungIndex, rungIndexOf } from '../shared/support.js';
import { placeChildInGame } from '../shared/profile.js';
import { createBags } from '../shared/lineBag.js';
import { createRng } from '../shared/rounds.js';
import useAttemptRecorder from '../shared/useAttemptRecorder.js';
import PipCharacter from '../characters/PipCharacter';

// This game's own.
import { createAdapter } from './adapter.js';
import { GAME, POOLS } from './content/index.js';
import { getCreature } from './content/creatures.js';
import * as VOICE from './content/voice.js';
import { childrenApi } from '../../../utils/api';

/** How long a door holds open before the next knock. */
const OPEN_MS = 700;

export default function DoorKnock({ onExit }) {
  const speech = useSpeechRecognition();
  const synth = useSpeechSynthesis();
  const fx = useAudioEffects(true);

  const env = useRef({});
  env.current = { ...env.current, speech, synth, fx, soundOn: true };

  const [screen, setScreen] = useState('start');
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [position, setPosition] = useState(null);
  const [view, setView] = useState({ doors: [], prompt: '', cue: null, chips: null, rungId: 'visual' });
  const [banner, setBanner] = useState(null);
  const [pip, setPip] = useState({ mood: 'happy', animation: 'idle' });

  // ---- the loop's own state, in refs. A setState that lands a frame late is a
  // nuisance in a UI and a corrupted trial record in a measurement instrument.
  const positionRef = useRef(null);
  const contentRef = useRef(null);
  const targetsRef = useRef([]);
  const supportIndexRef = useRef(2);
  const supportIncreasesRef = useRef(0);
  const attemptsRef = useRef([]);
  const levelScoreRef = useRef(null);
  const offerRef = useRef(null);
  const stepRef = useRef(null);
  const aliveRef = useRef(true);
  const bagsRef = useRef(null);
  const rngRef = useRef(null);
  const adapterRef = useRef(null);
  const stageRef = useRef(null);

  const recorder = useAttemptRecorder({
    childId: child ? child.id : null,
    gameId: GAME.id,
    onRecommendation: (rec, result) => {
      // The chart's own verdict on this level (section 7.5), which the local
      // evaluator cannot compute - it holds no box histories.
      if (result && result.level) levelScoreRef.current = result.level;
      // Computed from the child's whole history, so it beats anything this
      // session can see.
      applyPacing(rec);
    },
  });
  env.current.recorder = recorder;

  useEffect(() => {
    childrenApi.list().then((d) => setChildren(d.children || [])).catch(() => setChildren([]));
  }, []);

  const line = useCallback((pool, prefer) => {
    const bag = bagsRef.current && bagsRef.current[pool];
    return bag ? bag.next(prefer) : '';
  }, []);

  // ==========================================================================
  // THE STAGE - everything the trial loop needs from a real device
  // ==========================================================================
  if (!stageRef.current) {
    stageRef.current = createBrowserStage({
      env,
      post: (payload) => env.current.recorder.record(payload),
      show: (patch) => {
        if (patch.kind === 'prompt') {
          setView({
            doors: patch.target.onScreen.map((id) => ({
              id, open: false, lit: id === patch.target.creatureId,
            })),
            prompt: patch.prompt.text,
            cue: patch.prompt.cue ? patch.prompt.cue.text : null,
            chips: patch.prompt.chips,
            rungId: patch.rungId,
          });
          setPip((p) => ({ ...p, mood: 'curious' }));
          return;
        }
        if (patch.kind === 'ladder') {
          setView((v) => ({
            ...v,
            prompt: patch.prompt.text,
            cue: patch.prompt.cue ? patch.prompt.cue.text : null,
            rungId: patch.rungId,
          }));
          return;
        }
        if (patch.kind === 'hint') {
          setView((v) => ({ ...v, prompt: patch.text }));
          return;
        }
        if (patch.kind === 'listening') {
          setPip((p) => ({ ...p, mood: 'happy' }));
          setBanner(null);
        }
      },
    });
  }

  const say = useCallback((text) => stageRef.current.say(text), []);
  const pause = useCallback(
    (ms) => new Promise((resolve) => { stageRef.current.later(ms, resolve); }),
    []
  );

  // ==========================================================================
  // PACING - the engine's, never this game's
  // ==========================================================================
  function applyPacing(rec) {
    const phase = positionRef.current ? positionRef.current.phase : 2;
    const read = readRecommendation(rec, { phase, supportIndex: supportIndexRef.current });

    if (read.support && read.support.changed) {
      supportIndexRef.current = read.support.index;
      if (read.support.raise) {
        // Support before failure (section 2.4): a raise takes effect on the
        // very next trial, including a retry of the step in progress. A fade
        // deliberately does not - see shared/step.js.
        supportIncreasesRef.current += 1;
        if (stepRef.current) stepRef.current.supportRaised(read.support.index);
      }
    }
    // An offer is an offer: held until the level ends and presented there.
    if (read.offer) offerRef.current = read.offer;
  }

  // ==========================================================================
  // ONE STEP - one thing the child is asked for, and its trials
  // ==========================================================================
  const playStep = useCallback(async (index) => {
    const { phase, level } = positionRef.current;
    const content = contentRef.current;
    const target = targetsRef.current[index];
    const step = createStep({ phase, openingIndex: supportIndexRef.current });
    stepRef.current = step;

    for (;;) {
      const result = await runTrial({
        stage: stageRef.current,
        game: adapterRef.current,
        phase,
        level,
        content,
        target,
        pacing: adapterRef.current.pacing(phase, level),
        openingIndex: step.open(),
        attemptNumber: step.attemptNumber(),
        step: index + 1,
        supportIncreases: supportIncreasesRef.current,
      });
      if (result.cancelled || !aliveRef.current) return false;
      if (!result.ok) {
        // The game built something the engine would refuse. Nothing is posted -
        // a bad record is worse than no record - and the child plays on.
        // eslint-disable-next-line no-console
        console.error('[door-knock] the attempt was not posted:',
          result.validated.errors, result.check.errors);
      }

      // Whole-attempt outcomes at THIS level, which is what section 8.4 reads.
      attemptsRef.current = [...attemptsRef.current, result.outcome];
      applyPacing(evaluateProgression({
        phase,
        level,
        attempts: attemptsRef.current,
        supportIndex: supportIndexRef.current,
        supportIncreases: supportIncreasesRef.current,
        targetSupportIndex: rungIndexOf(content.nominalRung),
        levelScore: levelScoreRef.current,
        availablePhases: GAME.phasesAvailable,
        availableLevels: GAME.levelsAvailable,
      }));

      if (result.judged.success) {
        const creatureId = result.judged.detail.creatureId || target.creatureId;
        setView((v) => ({
          ...v,
          doors: v.doors.map((d) => (d.id === creatureId ? { ...d, open: true } : d)),
        }));
        env.current.fx.playSuccess?.();
        setPip({ mood: 'excited', animation: 'celebrate' });
        const praise = line('praise', 'bright');
        setBanner({ kind: 'praise', text: praise });
        await say(praise);
        await pause(OPEN_MS);
        setBanner(null);
        setPip({ mood: 'happy', animation: 'idle' });
        return true;
      }

      // Never a failure. Three different things can have just happened and they
      // deserve three different answers; only the last is anything like an error.
      const text = line(result.raw.transcript ? 'nearly' : 'encouragement', 'calm');
      setBanner({ kind: 'try', text });
      await say(text);
      if (!aliveRef.current) return false;

      if (!step.missed(result)) {
        // Pip does it with them. NOT recorded as an attempt: nothing was
        // measured, and writing a failure here would punish the child for the
        // game running out of patience.
        await say(line('assist'));
        await say(adapterRef.current.phraseFor(target));
        await pause(500);
        return true;
      }
      await pause(400);
      if (!aliveRef.current) return false;
    }
  }, [line, pause, say]);

  // ==========================================================================
  // ONE LEVEL
  // ==========================================================================
  const playLevel = useCallback(async (phase, level, { intro = false } = {}) => {
    const content = adapterRef.current.content(phase, level);
    if (!content) return null;

    const previous = positionRef.current;
    const sameLevel = Boolean(previous && previous.phase === phase && previous.level === level);

    positionRef.current = { phase, level };
    contentRef.current = content;
    targetsRef.current = adapterRef.current.plan(phase, level).flatMap((r) => r.targets);

    // A level is REPLAYED until its boxes have ten plays each, so its history
    // belongs to the LEVEL and not to one pass through it. Clearing it every
    // pass makes section 8.4's five-attempt window unreachable on any level
    // shorter than five trials, which is most of them.
    if (!sameLevel) {
      attemptsRef.current = [];
      supportIncreasesRef.current = 0;
      offerRef.current = null;
      levelScoreRef.current = null;
    }

    supportIndexRef.current = openingRungIndex({
      phase,
      levelNominalRung: content.nominalRung,
      placementIndex: supportIndexRef.current,
    });
    setPosition({ phase, level });

    if (intro) await say(line(`intro${phase}`));
    if (!aliveRef.current) return null;

    for (let i = 0; i < targetsRef.current.length; i += 1) {
      const carriedOn = await playStep(i);
      if (!carriedOn) return null;
    }

    await say(line('levelComplete'));
    if (!aliveRef.current) return null;

    const offer = offerRef.current;
    if (offer && offer.kind === 'complete') return null;
    // Nobody has to tap. With an offer standing, take it; otherwise play the
    // level again - ten plays is what a box needs before it can be coloured.
    return offer && offer.target ? offer.target : { phase, level };
  }, [line, playStep, say]);

  // ==========================================================================
  // A SESSION
  // ==========================================================================
  const begin = useCallback(async (chosen) => {
    setChild(chosen);
    rngRef.current = createRng(Math.floor(Math.random() * 2 ** 31) || 1);
    adapterRef.current = createAdapter({ rng: rngRef.current });
    bagsRef.current = createBags({
      ...POOLS,
      levelComplete: VOICE.LEVEL_COMPLETE,
      intro2: VOICE.LEVEL_INTRO[2],
      intro3: VOICE.LEVEL_INTRO[3],
      intro4: VOICE.LEVEL_INTRO[4],
    }, { rng: rngRef.current });

    // Section 9.2: ask the engine where to start, from the CHILD's phase and
    // level. The chart tracks the child, not the game (section 9.1).
    const placement = placeChildInGame(chosen, GAME);
    supportIndexRef.current = placement.supportIndex;

    env.current.speech.startListening();
    setScreen('play');

    let next = { phase: placement.phase, level: placement.level };
    let intro = true;
    while (next && aliveRef.current) {
      const from = next;
      // eslint-disable-next-line no-await-in-loop
      next = await playLevel(from.phase, from.level, { intro });
      intro = Boolean(next) && (next.phase !== from.phase || next.level !== from.level);
    }
    if (aliveRef.current) {
      env.current.speech.stopListening?.();
      env.current.recorder.flush();
      setScreen('end');
    }
  }, [playLevel]);

  // The recogniser's callbacks go to the stage, which queues them for the loop.
  useEffect(() => {
    aliveRef.current = true;
    const detach = stageRef.current.attach();
    return () => {
      aliveRef.current = false;
      detach();
      env.current.synth.cancel?.();
      env.current.speech.stopListening?.();
    };
  }, []);

  if (!speech.isSupported) {
    return (
      <div className="dk dk--message">
        <div className="dk__card">
          <h2>Door Knock needs a microphone</h2>
          <p>This game is played with your voice. Chrome, Edge or Safari will work.</p>
          <button type="button" className="dk__btn" onClick={onExit}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dk dk--${contentRef.current ? contentRef.current.scene : 'street'}`}>
      {screen === 'start' && (
        <div className="dk__card dk__start">
          <h1>Door Knock</h1>
          <p>Say who is behind the door and it opens.</p>
          {children.map((c) => (
            <button key={c.id} type="button" className="dk__btn dk__btn--child" onClick={() => begin(c)}>
              {c.name} · phase {c.currentPhase || 1}, level {c.currentLevel || 1}
            </button>
          ))}
          <button type="button" className="dk__btn" onClick={() => begin(null)}>
            Play without recording
          </button>
          <button type="button" className="dk__btn dk__btn--quiet" onClick={onExit}>Back to games</button>
        </div>
      )}

      {screen === 'play' && (
        <div className="dk__play">
          <div className="dk__street">
            {view.doors.map((door) => (
              <div key={door.id} className={`dk-door${door.open ? ' is-open' : ''}${door.lit ? ' is-lit' : ''}`}>
                <span className="dk-door__panel" />
                <span className="dk-door__who" role="img" aria-label={door.id}>
                  {getCreature(door.id).art}
                </span>
              </div>
            ))}
            {banner && <div className={`dk__banner dk__banner--${banner.kind}`}>{banner.text}</div>}
          </div>

          <div className="dk__pip">
            <PipCharacter mood={pip.mood} animation={pip.animation} size="medium" />
          </div>

          <div className="dk__prompt">
            <p className="dk__line">{view.prompt}</p>
            {view.cue && <span className="dk__cue">{view.cue}</span>}
            {view.chips && (
              <div className="dk__chips">
                {view.chips.map((c) => <span key={c.id} className="dk__chip">{c.label}</span>)}
                <span className="dk__chip dk__chip--blank">…</span>
              </div>
            )}
            {position && (
              <p className="dk__where">
                {getLevel(position.phase, position.level).phaseName} · {getPromptStyle(position.phase).style}
                {' · '}{getStrictness(position.phase).id}
              </p>
            )}
          </div>
        </div>
      )}

      {screen === 'end' && (
        <div className="dk__card">
          <h2>That is the whole street</h2>
          <button type="button" className="dk__btn" onClick={onExit}>Back to games</button>
        </div>
      )}
    </div>
  );
}
